import type { FC } from 'react';
import { FaCrosshairs, RiArrowRightSLine } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { wikiHref, type WikiT } from '../wikiText';
import type { WikiLang, WikiSearchHit, WikiSearchType } from '../wiki.types';
import { WikiRemoteImage } from './WikiRemoteImage';
import { WikiName } from './WikiName';

const HIT_PATH: Record<WikiSearchType, string> = {
  skin: '/wiki/skins/',
  weapon: '/wiki/weapons/',
  container: '/wiki/cases/',
  collection: '/wiki/collections/',
};

const HIT_LABEL: Record<WikiSearchType, 'typeSkin' | 'typeWeapon' | 'typeContainer' | 'typeCollection'> = {
  skin: 'typeSkin',
  weapon: 'typeWeapon',
  container: 'typeContainer',
  collection: 'typeCollection',
};

export const wikiHitHref = (lang: WikiLang, hit: WikiSearchHit): string => wikiHref(lang, `${HIT_PATH[hit.type]}${hit.slug}`);

export const WikiHitRow: FC<{ hit: WikiSearchHit; t: WikiT; active?: boolean; compact?: boolean }> = ({
  hit,
  t,
  active = false,
  compact = false,
}) => (
  <span className={cn('flex items-center gap-3 rounded-xl', compact ? 'min-h-[52px] px-2 py-1.5' : 'min-h-[94px] p-2 pe-3', active && 'bg-white/[0.08]')}>
    <span
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/[0.24] text-label-secondary ring-1 ring-inset ring-white/[0.16]',
        compact ? 'size-10' : 'h-[78px] w-[104px] rounded-[14px]',
      )}
      style={hit.rarityColor ? { boxShadow: `inset 0 -3px 0 ${hit.rarityColor}` } : undefined}
    >
      {hit.imageUrl ? (
        <WikiRemoteImage src={hit.imageUrl} alt="" sizes={compact ? '40px' : '104px'} className="p-1" />
      ) : (
        <FaCrosshairs aria-hidden size={compact ? 18 : 24} />
      )}
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className={cn('text-[15px] font-semibold leading-[20px] text-label-primary', compact ? 'truncate' : 'line-clamp-2')}>
        <WikiName name={hit.name} />
      </span>
      <span className="truncate text-[12px] leading-[16px] text-label-secondary">
        {t(HIT_LABEL[hit.type])}
        {hit.nameEn !== hit.name && <span dir="ltr"> · {hit.nameEn}</span>}
      </span>
    </span>
    {!compact && <RiArrowRightSLine aria-hidden size={20} className="shrink-0 text-label-secondary rtl:-scale-x-100" />}
  </span>
);
